import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Formik, Form, FastField } from 'formik';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';

import { successToast } from 'bxcommon/store/slices/toaster.slice';
import { register } from 'bxcommon/misc/requests';
import { BaseBox, BaseBoxHeading, BaseButtonFill, BaseCheckbox, BaseInput, BaseSelect, InputField } from 'bxcommon';

import errorsService, { REGISTRATION_ERRORS } from 'bxcommon/helpers/errors.helper';
import { requiredStringValidator, passwordValidator, phoneRegExp } from 'bxcommon/helpers/form.helper';
import { HalfPageLayout } from 'bxcommon/components/HalfPageLayout/HalfPageLayout';
import LoginMessages from 'bxcommon/components/LoginMessages/LoginMessages';
import useRecaptcha from 'bxcommon/hooks/useRecaptcha';

import { ProspectusHeader } from '../ProspectusHeader/ProspectusHeader';

const RightContent = ({
  initialFormValues,
  onSubmit,
  validationSchema,
  recaptchaRef,
  handleRecaptcha,
  registerLoading,
  capchaToken,
  countries,
  dialcodes,
  common,
  t,
}) => (
  <div>
    <div className="user-common-view__title">
      <h1 className="heading heading--primary heading--color-primary">{common('register.heading')}</h1>
      <div>{common('register.subheading')}</div>
    </div>
    <Formik initialValues={initialFormValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {(props) => (
        <>
          <BaseBox className="user-common-view__box" borderless>
            <BaseBoxHeading>{common('register.accountDetails')}</BaseBoxHeading>
            <Form className="form-container">
              <div className="row">
                <div className="col-1" />
                <div className="col-10">
                  <div className="row">
                    <InputField name="first_name" label={common('register.firstName')} />
                  </div>
                  <div className="row">
                    <InputField name="last_name" label={common('register.lastName')} />
                  </div>
                  <div className="user-common-view__label">{common('register.phoneNumber')}</div>
                  <div className="row">
                    <div className="col-6">
                      <FastField
                        component={BaseSelect}
                        name="dial_code"
                        selectOptions={dialcodes}
                        customKeys={{ label: 'dial', value: 'id' }}
                        popupClassName="dial-code"
                        data-testid="dial_code"
                      />
                    </div>
                    <div className="col-6">
                      <InputField name="phone" label={null} />
                    </div>
                  </div>
                  <div className="row">
                    <InputField name="email" label={t('register.workEmail')} />
                  </div>
                  <div className="row">
                    <FastField
                      component={BaseInput}
                      name="password"
                      type="password"
                      label={common('register.password')}
                      inputHint={common('passwordHint')}
                      data-testid="password"
                    />
                  </div>
                  <div className="row" data-testid="confirm-password-wrapper">
                    <FastField
                      component={BaseInput}
                      name="confirm_password"
                      type="password"
                      label={common('register.confirmPassword')}
                      data-testid="confirm_password"
                    />
                  </div>
                </div>
                <div className="col-1" />
              </div>
            </Form>
          </BaseBox>

          <BaseBox className="user-common-view__box" borderless>
            <BaseBoxHeading>{common('register.companyAddress')}</BaseBoxHeading>
            <Form className="form-container">
              <div className="row">
                <div className="col-1" />
                <div className="col-10">
                  <div className="row">
                    <InputField name="company_name" label={t('register.companyName')} />
                  </div>
                  <div className="row">
                    <InputField name="address_line_1" label={t('register.addressLine1')} />
                  </div>
                  <div className="row">
                    <InputField name="address_line_2" label={t('register.addressLine2')} />
                  </div>
                  <div className="row">
                    <div className="col-3">
                      <InputField name="postcode" label={t('register.postcode')} />
                    </div>
                    <div className="col-9">
                      <InputField name="city" label={t('register.city')} />
                    </div>
                  </div>
                  <div className="row">
                    <FastField
                      component={BaseSelect}
                      name="country"
                      label={t('register.country')}
                      selectOptions={countries}
                      customKeys={{ label: 'name', value: 'id' }}
                      popupClassName="country"
                      data-testid="country"
                    />
                  </div>
                  <div className="row user-common-view__consent">
                    <FastField
                      component={BaseCheckbox}
                      name="consent"
                      checked={props.values.consent}
                      data-testid="consent"
                    >
                      <Trans i18nKey="register.consent" ns="common">
                        <a
                          href="https://www.regservices.ch/?download=6313"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link--underlined"
                        >
                          User Agreement
                        </a>
                        <a
                          href="https://www.regservices.ch/en/privacy-policy/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link--underlined"
                        >
                          Privacy Policy
                        </a>
                      </Trans>
                    </FastField>
                  </div>

                  {!window.top.__E2E__ && (
                    <div className="row captcha" data-testid="captcha">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={import.meta.env.VITE_CAPTCHA_KEY}
                        onChange={handleRecaptcha}
                      />
                    </div>
                  )}

                  <div className="row user-common-view__submit">
                    <div className="col-12">
                      <BaseButtonFill
                        icon="arrow-right"
                        iconRight
                        full
                        type="submit"
                        loading={registerLoading}
                        disabled={!props.values.consent || (!window.top.__E2E__ && !capchaToken)}
                        data-testid="register-button"
                      >
                        {common('register.submitButton')}
                      </BaseButtonFill>
                    </div>
                  </div>
                </div>
                <div className="col-1" />
              </div>
            </Form>
          </BaseBox>
        </>
      )}
    </Formik>
  </div>
);

export const UserRegistration = () => {
  const { t } = useTranslation();
  const { t: common } = useTranslation('common');

  const navigate = useNavigate();

  const countries = useSelector((state) => state.resources.countries);
  const dialcodes = useSelector((state) => state.resources.dialcodes);

  const { capchaToken, recaptchaRef, handleRecaptcha } = useRecaptcha();
  const [registerLoading, setRegisterLoading] = useState(false);

  const initialFormValues = {
    first_name: '',
    last_name: '',
    dial_code: undefined,
    phone: '',
    email: '',
    password: '',
    confirm_password: '',
    company_name: '',
    address_line_1: '',
    address_line_2: '',
    postcode: '',
    city: '',
    country: undefined,
    consent: false,
  };

  const validationSchema = Yup.object().shape({
    first_name: requiredStringValidator(),
    last_name: requiredStringValidator(),
    phone: requiredStringValidator().matches(phoneRegExp, common('errors.phone')),
    dial_code: requiredStringValidator(),
    email: requiredStringValidator().email(common('errors.email')),
    password: passwordValidator(),
    confirm_password: passwordValidator().oneOf([Yup.ref('password'), null], common('errors.passwordsDontMatch')),
    company_name: requiredStringValidator(),
    address_line_1: requiredStringValidator(),
    postcode: requiredStringValidator(),
    city: requiredStringValidator(),
    country: requiredStringValidator(),
    consent: Yup.boolean().oneOf([true], common('errors.requiredField')),
  });

  const onSubmit = async (formData) => {
    if (capchaToken) {
      formData.last_login = new Date();

      try {
        setRegisterLoading(true);
        await register(formData);

        successToast(common('register.success.successDescription'), common('register.success.successTitle'));
        navigate('/login');
      } catch (error) {
        errorsService.handleErrorToast(error, REGISTRATION_ERRORS);
      } finally {
        setRegisterLoading(false);
      }
    }
  };

  return (
    <ProspectusHeader>
      <div className="user-common-view__content">
        <HalfPageLayout
          leftContent={<LoginMessages />}
          rightContent={
            <RightContent
              initialFormValues={initialFormValues}
              onSubmit={onSubmit}
              validationSchema={validationSchema}
              recaptchaRef={recaptchaRef}
              handleRecaptcha={handleRecaptcha}
              registerLoading={registerLoading}
              capchaToken={capchaToken}
              countries={countries}
              dialcodes={dialcodes}
              common={common}
              t={t}
            />
          }
        />
      </div>
    </ProspectusHeader>
  );
};

export default UserRegistration;
